"use client";

import { Fragment, useMemo } from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { SearchBar } from "@/components/search-bar";
import { BrandCard } from "@/components/brand-card";
import { Pagination } from "@/components/pagination";
import { BlogRecommendations } from "@/components/blog-recommendations";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { parseHeadquarters } from "@/lib/location-utils";
import type { Brand, Category, Company, BlogPost } from "@/lib/types";

interface CategoryDetailContentProps {
  category: Category;
  categoryBrands: Brand[];
  companies: Company[];
  recommendedPosts?: BlogPost[];
}

type SortOption = "name-asc" | "name-desc" | "founded";

export function CategoryDetailContent({
  category,
  categoryBrands,
  companies,
  recommendedPosts = [],
}: CategoryDetailContentProps) {
  const searchParams = useSearchParams();
  const currentPage = parseInt(searchParams.get("page") || "1", 10);
  const searchQuery = searchParams.get("q") || "";
  const sortBy = (searchParams.get("sort") || "name-asc") as SortOption;
  const selectedCountry = searchParams.get("country") || "all";

  const ITEMS_PER_PAGE = 50;

  // Create a map of company slugs to company objects for quick lookup
  const companyMap = companies.reduce((acc, company) => {
    acc[company.slug] = company;
    return acc;
  }, {} as Record<string, Company>);

  // Extract unique countries from category brands for the dropdown
  const uniqueCountries = useMemo(() => {
    const countrySet = new Set<string>();
    for (const brand of categoryBrands) {
      if (brand.headquarters) {
        const loc = parseHeadquarters(brand.headquarters);
        if (loc.country) countrySet.add(loc.country);
      }
    }
    return Array.from(countrySet).sort();
  }, [categoryBrands]);

  // Helper to build URL params preserving all current filters
  const buildParams = (overrides: Record<string, string> = {}) => {
    const params = new URLSearchParams();
    const values: Record<string, string> = {
      q: searchQuery,
      sort: sortBy,
      country: selectedCountry,
      ...overrides,
    };
    if (values.q) params.set("q", values.q);
    if (values.sort && values.sort !== "name-asc") params.set("sort", values.sort);
    if (values.country && values.country !== "all") params.set("country", values.country);
    return params.toString();
  };

  // Filter and sort brands
  const filteredAndSortedBrands = useMemo(() => {
    let filtered = categoryBrands.filter(brand =>
      brand?.name &&
      brand?.slug &&
      typeof brand.slug === "string" &&
      brand.slug.trim() !== "" &&
      brand.name.trim() !== ""
    );

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (brand) =>
          brand.name.toLowerCase().includes(query) ||
          brand.description.toLowerCase().includes(query) ||
          brand.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    // Apply country filter
    if (selectedCountry !== "all") {
      filtered = filtered.filter(brand => {
        if (!brand.headquarters) return false;
        const loc = parseHeadquarters(brand.headquarters);
        return loc.country === selectedCountry;
      });
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case "name-asc":
          return a.name.localeCompare(b.name);
        case "name-desc":
          return b.name.localeCompare(a.name);
        case "founded":
          return (b.foundedYear || 0) - (a.foundedYear || 0);
        default:
          return 0;
      }
    });

    return sorted;
  }, [categoryBrands, searchQuery, sortBy, selectedCountry]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredAndSortedBrands.length / ITEMS_PER_PAGE);
  const validPage = Math.min(Math.max(1, currentPage), totalPages || 1);
  const startIndex = (validPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedBrands = filteredAndSortedBrands.slice(startIndex, endIndex);

  // Build query params for pagination
  const queryParams: Record<string, string> = {};
  if (searchQuery) queryParams.q = searchQuery;
  if (sortBy !== "name-asc") queryParams.sort = sortBy;
  if (selectedCountry !== "all") queryParams.country = selectedCountry;

  return (
    <div className="py-8 md:py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Breadcrumbs
          items={[
            { name: "Home", href: "/" },
            { name: "Categories", href: "/categories" },
            { name: category.name },
          ]}
        />

        {/* Header */}
        <header className="mb-12">
          <h1 className="text-3xl md:text-4xl font-serif font-semibold text-foreground">
            {category.name} Brands
          </h1>
          <p className="mt-4 text-lg text-muted-foreground leading-relaxed max-w-3xl">
            {category.seoDescription}
          </p>
        </header>

        {/* About Section */}
        <section className="mb-12 p-6 bg-muted rounded-lg border border-border">
          <h2 className="text-xl font-serif font-semibold text-foreground mb-4">
            About {category.name}
          </h2>
          <p className="text-foreground leading-relaxed">{category.description}</p>
        </section>

        {/* Search */}
        <div className="mb-12 max-w-xl">
          <SearchBar
            placeholder="Search brands in this category..."
            initialData={{
              brands: categoryBrands,
              companies: companies,
              categories: [category],
            }}
          />
        </div>

        {/* Filters and Sort */}
        <div className="mb-8 flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-wrap">
          {/* Country Filter */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              Filter by Country
            </label>
            <select
              value={selectedCountry}
              onChange={(e) => {
                window.location.href = `/categories/${category.slug}?${buildParams({ country: e.target.value })}`;
              }}
              className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm"
            >
              <option value="all">All Countries</option>
              {uniqueCountries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
          </div>

          {/* Sort */}
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-foreground">
              Sort by
            </label>
            <select
              value={sortBy}
              onChange={(e) => {
                window.location.href = `/categories/${category.slug}?${buildParams({ sort: e.target.value })}`;
              }}
              className="px-3 py-2 rounded-md border border-border bg-background text-foreground text-sm"
            >
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="founded">Founded Year</option>
            </select>
          </div>

          {/* Results count */}
          <div className="text-sm text-muted-foreground sm:ml-auto">
            Showing {paginatedBrands.length > 0 ? startIndex + 1 : 0}-
            {Math.min(endIndex, filteredAndSortedBrands.length)} of{" "}
            {filteredAndSortedBrands.length} brands
          </div>
        </div>

        {/* Brands Grid */}
        {paginatedBrands.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
              {paginatedBrands.map((brand) => (
                <BrandCard
                  key={brand.slug}
                  brand={brand}
                  company={companyMap[brand.companySlug]}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <Pagination
                currentPage={validPage}
                totalPages={totalPages}
                baseUrl={`/categories/${category.slug}`}
                queryParams={queryParams}
              />
            )}
          </>
        ) : (
          <div className="text-center py-12 bg-muted rounded-lg border border-border">
            <p className="text-lg text-muted-foreground">
              {searchQuery
                ? "No brands found matching your search."
                : "No brands found in this category yet."}
            </p>
            <Link
              href="/browse"
              className="mt-4 inline-block px-4 py-2 text-sm font-medium bg-accent text-accent-foreground rounded-md hover:bg-accent/90 transition-colors"
            >
              Browse all brands
            </Link>
          </div>
        )}

        {/* Blog Recommendations */}
        {recommendedPosts.length > 0 && (
          <div className="mt-12">
            <BlogRecommendations
              posts={recommendedPosts}
              title={`Articles About ${category.name}`}
              maxPosts={3}
            />
          </div>
        )}
      </div>
    </div>
  );
}
